import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";

function formatTodayISO() {
  return new Date().toISOString().slice(0, 10);
}

function normalizeUnit(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/-/g, "")
    .replace(/\s+/g, "")
    .trim();
}

export default function ResponderUnitInfo({
  unitName,
  unitStatus,
  activeCall,
  onOpenInventory,
  onOpenSupplyRequest,
  onOpenReports,
  onOpenCotak,
  onChangeUnit,
}) {
  const [crew, setCrew] = useState([]);
  const [inventoryStatus, setInventoryStatus] =
    useState("PENDING");

  const todayISO = useMemo(
    () => formatTodayISO(),
    []
  );

  useEffect(() => {
    async function loadData() {
      if (!unitName) return;

      try {
        const normalized =
          normalizeUnit(unitName);

        //
        // INVENTORY STATUS
        //
        const inventorySnap = await getDocs(
  query(
    collection(db, "dailyInventories"),
    where("locationId", "==", normalized),
    where("inventoryDate", "==", todayISO)
  )
);

       if (inventorySnap.empty) {
  setInventoryStatus("PENDING");
} else {
  const inventoryData = inventorySnap.docs[0].data();

  if (inventoryData.outOfService === true) {
    setInventoryStatus("OOS");
  } else if (inventoryData.status === "submitted") {
    setInventoryStatus("COMPLETE");
  } else if (inventoryData.status === "in_progress") {
    setInventoryStatus("IN PROGRESS");
  } else if (inventoryData.actionRequired) {
    setInventoryStatus("ACTION REQUIRED");
  } else {
    setInventoryStatus("PENDING");
  }
}

        //
        // CREW
        //
        const scheduleSnap =
          await getDocs(
            query(
              collection(
                db,
                "scheduleAssignments"
              ),
              where("date", "==", todayISO)
            )
          );

        const matchingCrew = [];

        scheduleSnap.forEach((docSnap) => {
          const data = docSnap.data();

          const assignment =
            normalizeUnit(
              data.assignment ||
                data.unit ||
                data.location
            );

          if (assignment === normalized) {
            matchingCrew.push({
              id: docSnap.id,
              name:
                data.employeeName ||
                data.name ||
                "Unnamed",
              position:
                data.position ||
                data.role ||
                "",
            });
          }
        });

        setCrew(matchingCrew);
      } catch (err) {
        console.error(
          "Failed loading responder data:",
          err
        );
      }
    }

    loadData();
  }, [unitName, todayISO]);

  function getInventoryColor() {
  if (inventoryStatus === "COMPLETE") return "#15803d";

  if (inventoryStatus === "IN PROGRESS") return "#2563eb";

  if (inventoryStatus === "ACTION REQUIRED") return "#b45309";

  if (inventoryStatus === "OOS") return "#374151";

  return "#dc2626";
}

  function getCallStatus() {
    if (!activeCall) return "";

    const status = String(
      activeCall?.status || ""
    ).toLowerCase();

    if (
      [
        "requested",
        "dispatched",
        "responding",
      ].includes(status)
    ) {
      return "RESPONDING";
    }

    if (status === "onscene") {
      return "ON-SCENE";
    }

    if (
      status === "transporting"
    ) {
      return "TRANSPORTING";
    }

    if (
      [
        "arrivedhq",
        "arrived_hq",
      ].includes(status)
    ) {
      return "ARRIVED HQ";
    }

    return status.toUpperCase();
  }

  return (
    <div style={styles.wrapper}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.unitCard}>
          <div style={styles.unitTitle}>
            {unitName}
          </div>

          <div style={styles.sectionLabel}>
            Assigned Crew
          </div>

          {crew.length === 0 ? (
            <div style={styles.emptyText}>
              No crew assigned
            </div>
          ) : (
            crew.map((person) => (
              <div
                key={person.id}
                style={styles.crewCard}
              >
                <strong>
                  {person.name}
                </strong>

                <div
                  style={styles.crewRole}
                >
                  {person.position}
                </div>
              </div>
            ))
          )}

          <div style={styles.buttonStack}>
            <button
              style={{
                ...styles.sideButton,
                borderLeft: `8px solid ${getInventoryColor()}`,
              }}
              onClick={
                onOpenInventory
              }
            >
              <div>
                OPEN INVENTORY
              </div>

              <div
                style={
                  styles.buttonSub
                }
              >
                {inventoryStatus}
              </div>
            </button>

            <button
              style={
                styles.sideButton
              }
              onClick={
                onOpenSupplyRequest
              }
            >
              REQUEST SUPPLIES
            </button>

            <button
              style={
                styles.sideButton
              }
              onClick={onOpenReports}
            >
              REPORTS
            </button>

            <button
              style={
                styles.sideButton
              }
              onClick={onOpenCotak}
            >
              OPEN iTAK
            </button>

            <button
              style={
                styles.sideButton
              }
              onClick={onChangeUnit}
            >
              CHANGE UNIT
            </button>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={styles.main}>
        {activeCall ? (
          <div style={styles.callCard}>
            <div style={styles.callContent}>
              {/* LOCATION */}
              <div style={styles.locationText}>
                {activeCall?.location ||
                  activeCall?.pickup ||
                  "Unknown Location"}
              </div>

              {/* NATURE */}
              <div style={styles.natureText}>
                {activeCall?.nature ||
                  "Call"}
              </div>

              {/* STATUS */}
              <div style={styles.statusBar}>
                {getCallStatus()}
              </div>

              {/* NOTES */}
              {(activeCall?.notes ||
                activeCall?.description) && (
                <div
                  style={styles.notesBox}
                >
                  {activeCall?.notes ||
                    activeCall?.description}
                </div>
              )}

             {/* RESPONDING UNITS */}
<div style={styles.unitsSection}>
  <div style={styles.unitsHeader}>RESPONDING UNITS</div>

  <div style={styles.unitsPillRow}>
    {Object.entries(activeCall?.unitStatuses || {}).map(
      ([unitId, status]) => (
        <div key={unitId} style={styles.unitPill}>
          <div style={styles.unitPillName}>
            {unitId
              .replace(/^cart(\d+)$/i, "Cart $1")
              .replace(/^bike(\d+)$/i, "Bike $1")
              .replace(/^transport(\d+)$/i, "Transport $1")
              .replace(/^supervisor(\d+)$/i, "Supervisor $1")
              .replace(/^command(\d+)$/i, "Command $1")}
          </div>

          <div style={styles.unitPillStatus}>
            {String(status)
              .replace("DISP", "RESPONDING")
              .replace("ON_SCENE", "ON-SCENE")
              .replace("TO_HQ", "TRANSPORTING")
              .replace("ARRIVED_HQ", "ARRIVED HQ")}
          </div>
        </div>
      )
    )}

    {Object.keys(activeCall?.unitStatuses || {}).length === 0 && (
      <div style={styles.otherUnits}>{unitName}</div>
    )}
  </div>
</div>
            </div>
          </div>
        ) : (
          <div style={styles.noCall}>
  <div
    style={{
      ...styles.availableLabel,
      color:
        unitStatus === "OOS"
          ? "#dc2626"
          : "#15803d",
    }}
  >
    {unitStatus === "OOS"
      ? "OUT OF SERVICE"
      : "AVAILABLE"}
  </div>

  <div style={styles.availableSub}>
    {unitStatus === "OOS"
      ? "Unit Unavailable"
      : "No Active Call"}
  </div>
</div>
        )}
      </div>
    </div>
  );
}

const isPhone = window.innerWidth <= 640;
const isTablet = window.innerWidth <= 700;

const styles = {
wrapper: {
  display: "grid",
  gridTemplateColumns: isTablet ? "1fr" : "280px 1fr",
  gap: 12,
  height: isTablet ? "auto" : "calc(100vh - 24px)",
  overflow: "auto",
},

  sidebar: {
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
},

  unitCard: {
    background: "#fff",
    borderRadius: 18,
    padding: 14,
    border: "1px solid #dbe4ee",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  unitTitle: {
    fontSize: 34,
    fontWeight: 800,
    marginBottom: 10,
    color: "#0f172a",
  },

  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    marginBottom: 6,
    textTransform:
      "uppercase",
  },

  crewCard: {
    background: "#f8fafc",
    border:
      "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 8,
    marginBottom: 6,
  },

  crewRole: {
    color: "#64748b",
    fontSize: 11,
    marginTop: 2,
  },

  emptyText: {
    color: "#64748b",
    marginBottom: 10,
    fontSize: 14,
  },

  buttonStack: {
    display: "grid",
    gap: 6,
    marginTop: 10,
  },

  sideButton: {
  minHeight: isPhone ? 64 : 52,
  borderRadius: 14,
  border: "none",
  background: "#0f766e",
  color: "#fff",
  fontWeight: 800,
  fontSize: isPhone ? 22 : 15,
  cursor: "pointer",
  textAlign: "left",
  paddingLeft: 18,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
},

  buttonSub: {
    fontSize: 10,
    marginTop: 1,
    opacity: 0.9,
  },

  main: {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: isTablet ? 400 : "auto",
},

  noCall: {
    height: "100%",
    width: "100%",
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #dbe4ee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
  },

  availableLabel: {
  fontSize: isPhone ? 44 : 72,
    fontWeight: 900,
    color: "#15803d",
    lineHeight: 1,
  },

  availableSub: {
    fontSize: 24,
    color: "#64748b",
    fontWeight: 700,
  },

  callCard: {
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #dbe4ee",
    padding: 40,
    width: "100%",
    maxWidth: 1200,
    minHeight: 420,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  callContent: {
    width: "100%",
    maxWidth: 900,
    margin: "0 auto",
  },

  locationText: {
  fontSize: isPhone ? 34 : 54,
    fontWeight: 900,
    textAlign: "center",
    color: "#0f172a",
    lineHeight: 1.1,
  },

 natureText: {
  marginTop: 12,
  fontSize: isPhone ? 24 : 34,
    textAlign: "center",
    color: "#475569",
    fontWeight: 700,
  },

 statusBar: {
  marginTop: 28,
  background: "#0f766e",
  color: "#fff",
  borderRadius: 16,
  padding: isPhone
    ? "14px 18px"
    : "18px 24px",
  textAlign: "center",
  fontSize: isPhone ? 24 : 34,
  fontWeight: 900,
},

  notesBox: {
    marginTop: 28,
    background: "#f1f5f9",
    borderRadius: 16,
    padding: 24,
    fontSize: 28,
    color: "#0f172a",
    textAlign: "center",
  },

  unitsSection: {
    marginTop: 30,
  },

  unitsHeader: {
    fontSize: 15,
    fontWeight: 800,
    color: "#64748b",
    marginBottom: 10,
    textTransform:
      "uppercase",
  },

  unitsPillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },

  unitPill: {
    background: "#eef2f7",
    borderRadius: 12,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  unitPillName: {
    fontWeight: 800,
    fontSize: 16,
    color: "#0f172a",
  },

  unitPillStatus: {
    fontWeight: 800,
    fontSize: 13,
    color: "#0f766e",
  },

  otherUnits: {
    fontSize: 16,
    color: "#475569",
  },
};